import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { apiService } from '../../services/api';
import ChatBubble from './ChatBubble';
import RunSteps from './RunSteps';
import { applyActivity, applyNote, closeRun, startRun } from './stepModel';
import ChatInput from './ChatInput';
import { updateHermesDraft, clearHermesDraft, settleConfirmations } from './hermesStream';
import ConfirmationPrompt from './ConfirmationPrompt';
import ProactivityPrompt from './ProactivityPrompt';
import { NexusNoticeCard, useNexusNotices } from './NexusNotices';
import MuseChat, { useMuseConversation, useMuseStatus } from './MuseChat';
import { readSseEvents } from './sse';
import { useAuth } from '../Auth';
import { useNexusSync } from '../../context/NexusSyncContext';
import { useNexusPageDetails } from '../../context/nexusPage';
import { buildPageContext } from '../../context/pageContext';
import { Bug, Loader2, Maximize2, Minimize2, SquarePen, Sparkles, X } from 'lucide-react';
import NexusModelSwitch from './NexusModelSwitch';
import useVisibleViewport from './useVisibleViewport';
import NexusBot from './NexusBot';

const AUTOMATION_POLL_INTERVAL_MS = 8000;
const CHAT_MODE_STORAGE_KEY = 'deepflow.chatMode';
const CHAT_SIZE_STORAGE_KEY = 'deepflow.chatExpanded';

const museStatusDotClasses = {
  online: 'bg-emerald-400',
  offline: 'bg-rose-400',
  unconfigured: 'bg-slate-400',
  unknown: 'bg-slate-300',
};

const museStatusLabels = {
  online: 'en ligne',
  offline: 'hors ligne',
  unconfigured: 'non relié',
  unknown: 'connexion…',
};

// « muse-tools » : essai de Muse avec les outils de Nexus (administrateurs), dans le panneau de Nexus.
const CHAT_MODES = ['nexus', 'muse', 'muse-tools'];
const readStoredChatMode = () => {
  try {
    const stored = window.localStorage.getItem(CHAT_MODE_STORAGE_KEY);
    return CHAT_MODES.includes(stored) ? stored : 'nexus';
  } catch {
    return 'nexus';
  }
};

const MUSE_TOOLS_GREETING = {
  content: "Muse ici, avec les outils de Nexus (essai) : je peux lire et modifier Deepflow, avec ta confirmation avant "
    + "chaque modification. Je tourne sur le Mac mini, donc je suis plus lente que Nexus.",
  role: 'assistant',
  agent: 'muse',
};

const greetingPool = [
  'Salut ! On se fait un point rapide ?',
  'Hey ! Besoin d’un coup de main ?',
  'Coucou, je suis prêt à faire avancer tes idées.',
  'On s’y met ? Dis-moi ce qui t’intéresse.',
  'Hello ! Partant pour éclaircir ta to-do ?',
  'Content de te revoir ! On commence par quoi ?',
  'Nexus au rapport. Tu veux analyser ou agir ?',
  'Je suis là, prêt à explorer ce qui t’occupe.',
  'Besoin d’un check express ? Je peux m’en charger.',
  'On décortique une question ? Je suis dispo.',
  'Yo ! On passe en mode action ?',
  'Prêt quand tu l’es. On s’attele à ton prochain sujet ?',
  'On aligne nos idées ? Lance la première.',
  'Salut ! Tu veux un résumé, une décision, un plan ?',
  'Je peux creuser n’importe quelle piste, dis-moi laquelle.',
  'Envie de clarifier quelque chose ? Je t’écoute.',
  'On se met en route, tu me briefe ?',
  'Nexus en ligne. On dynamise ta prochaine action ?',
  'Un doute, une idée, une question : je suis sur le pont.',
  'C’est parti ! Tu veux qu’on commence par quoi ?',
];

const getRandomGreeting = () => ({
  content: greetingPool[Math.floor(Math.random() * greetingPool.length)],
  role: 'assistant',
  agent: 'nexus',
});

const generateConversationId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `conv-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const formatAutomationTimestamp = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const normalizeAutomationMetadata = (raw) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  return raw;
};

const buildAutomationMessageContent = (run) => {
  const metadata = normalizeAutomationMetadata(run.metadata);
  const response = (run.response_message || '').trim();
  const metadataMessage = (metadata.message || '').trim();
  const fallbackPrompt = (metadata.prompt || '').trim();
  const condition = (metadata.condition || '').trim();
  const baseText = response || metadataMessage || fallbackPrompt || "Nexus n'a pas fourni de message.";
  const lines = [];

  if (run.status === 'error') {
    const errorText = (run.error_message || '').trim() || "L'automatisation n'a pas abouti.";
    lines.push(`⚠️ ${errorText}`);
    if (baseText && baseText !== errorText) {
      lines.push(baseText);
    }
  } else if (baseText) {
    lines.push(baseText);
  }

  if (condition) {
    lines.push(`_(Condition suivie : ${condition})_`);
  }

  const footerParts = [];
  if (run.automation_title) {
    footerParts.push(`automatisation « ${run.automation_title} »`);
  }
  const timestampLabel = formatAutomationTimestamp(run.triggered_at);
  if (timestampLabel) {
    footerParts.push(`déclenchée le ${timestampLabel}`);
  }
  if (footerParts.length) {
    lines.push(`_${footerParts.join(' · ')}_`);
  }

  return lines.filter(Boolean).join('\n\n');
};

const resolveActivityType = (phase) => {
  if (phase === 'tool_result') return 'tool_result';
  if (phase === 'tool_call') return 'tool_call';
  if (phase === 'routing') return 'routing';
  if (phase === 'confirmation') return 'confirmation';
  return 'tool_call';
};

// Suggestions d'une conversation vide, selon la page ouverte.
const suggestionsFor = (pageContext) => {
  const items = ['Quoi de neuf sur mes projets ?', "Qu'est-ce que j'ai à faire aujourd'hui ?"];
  if (pageContext?.extra?.projectId) {
    items.unshift('Fais-moi le point sur ce projet');
  } else {
    items.push('Cherche dans les documents d’un projet…');
  }
  return items.slice(0, 3);
};

const readStoredExpanded = () => {
  try {
    return window.localStorage.getItem(CHAT_SIZE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const ChatBot = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([getRandomGreeting()]);
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  // Étapes de la réponse en cours (recherches, lectures…) ; rattachées au message final.
  const [liveRun, setLiveRun] = useState(null);
  const liveRunRef = useRef(null);
  const [expanded, setExpanded] = useState(readStoredExpanded);
  const phoneBox = useVisibleViewport(isOpen);
  // Humeur du robot juste après une réponse (content) ou une erreur (déçu), quelques secondes.
  const [botFlash, setBotFlash] = useState(null);
  const botFlashTimer = useRef(null);
  const flashBot = useCallback((mood) => {
    setBotFlash(mood);
    clearTimeout(botFlashTimer.current);
    botFlashTimer.current = setTimeout(() => setBotFlash(null), mood === 'happy' ? 2600 : 3500);
  }, []);
  const [conversationId, setConversationId] = useState(() => generateConversationId());
  const [clearingConversation, setClearingConversation] = useState(false);
  // La même fenêtre bascule entre Nexus et Muse (modèle local du Mac mini) ; chacun garde sa conversation.
  // Essai de Muse avec outils : administrateurs (dont le super-administrateur, employé n° 1).
  const canTryMuseTools = Boolean(user?.is_admin) || Number(user?.employee_id ?? user?.id) === 1;
  const [chatMode, setChatMode] = useState(() => {
    const stored = readStoredChatMode();
    return stored === 'muse-tools' && !canTryMuseTools ? 'muse' : stored;
  });
  const chatModeRef = useRef(chatMode);
  chatModeRef.current = chatMode;
  const museConversation = useMuseConversation();
  const museStatus = useMuseStatus(isOpen);
  const [bugReportInitiating, setBugReportInitiating] = useState(false);
  const [draft, setDraft] = useState(null);
  const pageDetails = useNexusPageDetails();

  const updateRun = useCallback((change) => {
    liveRunRef.current = change(liveRunRef.current);
    setLiveRun(liveRunRef.current);
  }, []);

  // Étapes terminées, à garder avec le message qui clôt la réponse (réponse, confirmation, erreur).
  const takeRun = useCallback(() => {
    const run = liveRunRef.current ? closeRun(liveRunRef.current) : null;
    liveRunRef.current = null;
    setLiveRun(null);
    return run;
  }, []);

  // Une réponse en cours a toujours ses étapes (même reprise après un rechargement du flux).
  useEffect(() => {
    if (loading && !liveRunRef.current) updateRun(() => startRun());
  }, [loading, updateRun]);

  useEffect(() => {
    try {
      window.localStorage.setItem(CHAT_SIZE_STORAGE_KEY, expanded ? '1' : '0');
    } catch {
      // Stockage indisponible : la taille revient simplement à la normale.
    }
  }, [expanded]);

  useEffect(() => {
    try {
      window.localStorage.setItem(CHAT_MODE_STORAGE_KEY, chatMode);
    } catch {
      // Stockage indisponible (navigation privée) : le mode reviendra simplement à Nexus.
    }
  }, [chatMode]);

  // Une page peut ouvrir le chat avec une demande préremplie (ex. appliquer les commentaires d'une offre).
  useEffect(() => {
    const handler = (event) => {
      setIsOpen(true);
      setChatMode('nexus');
      if (event.detail?.message) setDraft({ text: event.detail.message });
    };
    window.addEventListener('nexus-open', handler);
    return () => window.removeEventListener('nexus-open', handler);
  }, []);
  const [proactivityRequests, setProactivityRequests] = useState([]);
  const [proactivityLoading, setProactivityLoading] = useState(false);
  const [respondingProactivityId, setRespondingProactivityId] = useState(null);
  const [automationUnread, setAutomationUnread] = useState(0);
  const [nexusAlertPending, setNexusAlertPending] = useState(false);
  const messagesEndRef = useRef(null);
  const readerRef = useRef(null);
  // Hermes travaille au-delà de l'attente du serveur : le chat relance le suivi tout seul.
  const resumeRef = useRef({ pending: false, count: 0 });
  const streamMessageRef = useRef(null);
  const pendingConfirmationRef = useRef(null);
  const conversationIdRef = useRef(conversationId);
  // Nexus et Muse avec outils partagent le panneau, chacun avec sa conversation (gardée en passant de l'un à l'autre).
  const threadsRef = useRef({});
  const lastPanelRef = useRef(chatMode === 'muse' ? 'nexus' : chatMode);
  const deepflowProjectRef = useRef({ id: null, promise: null });
  const messagesRef = useRef(messages);
  const activityLogRef = useRef(activityLog);
  const proactivityIntervalRef = useRef(null);
  const proactivityIdSetRef = useRef(new Set());
  const automationDeliveredRef = useRef(new Set());
  const automationAckPendingRef = useRef(new Set());
  const proactivityCountRef = useRef(0);
  const automationCountRef = useRef(0);
  const isOpenRef = useRef(isOpen);
  const { publishMutation, lastMutation } = useNexusSync();

  // Messages posés par Nexus (ex. relier Google) ; seuls ceux pas encore vus dans le chat sont signalés sur le bouton.
  const { notices, unseen: unseenNotices, dismiss: dismissNotice, markSeen: markNoticesSeen } = useNexusNotices();
  const noticeCountRef = useRef(0);

  const updateAlertBadge = useCallback(() => {
    const hasItems = proactivityCountRef.current > 0 || automationCountRef.current > 0 || noticeCountRef.current > 0;
    if (!isOpenRef.current && hasItems) {
      setNexusAlertPending(true);
    } else if (isOpenRef.current && !hasItems) {
      setNexusAlertPending(false);
    }
  }, []);

  useEffect(() => {
    noticeCountRef.current = unseenNotices.length;
    updateAlertBadge();
  }, [unseenNotices.length, updateAlertBadge]);

  useEffect(() => {
    if (isOpen && chatMode === 'nexus' && unseenNotices.length) markNoticesSeen();
  }, [isOpen, chatMode, unseenNotices.length, markNoticesSeen]);

  // Chat refermé : les messages sans action déjà vus sont lus (ceux qui demandent une action restent jusqu'à elle).
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) notices.filter((n) => n.seen_at && !n.action_url).forEach(dismissNotice);
    wasOpenRef.current = isOpen;
  }, [isOpen, notices, dismissNotice]);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    activityLogRef.current = activityLog;
  }, [activityLog]);

  useEffect(() => {
    isOpenRef.current = isOpen;
    if (isOpen) {
      if (automationUnread !== 0) {
        automationCountRef.current = 0;
        setAutomationUnread(0);
      } else if (automationCountRef.current !== 0) {
        automationCountRef.current = 0;
      }
      updateAlertBadge();
    }
  }, [isOpen, automationUnread, updateAlertBadge]);

  const activityLabels = useMemo(
    () => ({
      start: 'Analyse',
      routing: 'Routage',
      tool_call: 'Appel outil',
      tool_result: 'Résultat outil',
      confirmation: 'Confirmation',
      completed: 'Réponse prête',
      error: 'Erreur',
    }),
    []
  );

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading, chatMode, liveRun]);

  // Téléphone : le clavier s'ouvre (zone visible plus petite) → on garde le dernier message en vue.
  const phoneHeight = phoneBox?.height;
  useEffect(() => {
    if (phoneHeight && messagesEndRef.current) messagesEndRef.current.scrollIntoView({ block: 'end' });
  }, [phoneHeight]);

  useEffect(() => {
    if (isOpen) {
      setNexusAlertPending(false);
    }
  }, [isOpen]);

  useEffect(() => () => {
    if (readerRef.current) {
      readerRef.current.cancel().catch(() => {});
    }
    if (proactivityIntervalRef.current) {
      clearInterval(proactivityIntervalRef.current);
    }
  }, []);

  const publishToolMutation = useCallback(
    (mutation) => {
      if (!publishMutation || !mutation) {
        return;
      }
      const metadata = mutation.metadata;
      if (!metadata || Object.keys(metadata).length === 0) {
        return;
      }
      publishMutation({
        source: 'nexus',
        tool: mutation.tool,
        metadata,
        summary: mutation.summary,
      });
    },
    [publishMutation]
  );

  const loadProactivityRequests = useCallback(
    async (options = {}) => {
      const { silent = false } = options;
      if (!silent) {
        setProactivityLoading(true);
      }
      try {
        const data = await apiService.getNexusProactivityRequests();
        const nextRequests = data?.requests ?? [];
        const nextIds = new Set(nextRequests.map((item) => item.id));
        proactivityIdSetRef.current = nextIds;
        setProactivityRequests(nextRequests);
        proactivityCountRef.current = nextRequests.length;
        updateAlertBadge();
      } catch (error) {
        console.warn('Impossible de charger les demandes proactives Nexus:', error);
      } finally {
        if (!silent) {
          setProactivityLoading(false);
        }
      }
    },
    [updateAlertBadge]
  );

  const appendAutomationRuns = useCallback((runs) => {
    if (!Array.isArray(runs) || runs.length === 0) {
      return;
    }
    setMessages((prev) => [
      ...prev,
      ...runs.map((run) => ({
        content: buildAutomationMessageContent(run),
        role: 'assistant',
        agent: 'nexus',
        automationRunId: run.id,
      })),
    ]);
  }, []);

  const acknowledgeAutomationRuns = useCallback(async (runs) => {
    if (!Array.isArray(runs) || runs.length === 0) {
      return;
    }
    await Promise.all(
      runs.map((run) =>
        apiService
          .acknowledgeAutomationMessage(run.id)
          .catch((error) => console.warn("Impossible d'accuser réception du message automatique:", error))
          .finally(() => {
            automationAckPendingRef.current.delete(run.id);
          })
      )
    );
  }, []);

  const loadAutomationMessages = useCallback(
    async () => {
      try {
        const data = await apiService.getAutomationMessages();
        const runs = Array.isArray(data) ? data : [];

        if (runs.length === 0) {
          if (isOpenRef.current && automationCountRef.current !== 0) {
            automationCountRef.current = 0;
            if (automationUnread !== 0) {
              setAutomationUnread(0);
            }
            updateAlertBadge();
          }
          return;
        }

        const newRuns = runs.filter((run) => !automationDeliveredRef.current.has(run.id));
        if (newRuns.length > 0) {
          newRuns.forEach((run) => {
            automationDeliveredRef.current.add(run.id);
            automationAckPendingRef.current.add(run.id);
          });
          appendAutomationRuns(newRuns);

          if (isOpenRef.current) {
            if (automationCountRef.current !== 0 || automationUnread !== 0) {
              automationCountRef.current = 0;
              setAutomationUnread(0);
            }
          } else {
            setAutomationUnread((prev) => {
              const next = prev + newRuns.length;
              automationCountRef.current = next;
              return next;
            });
          }
        }

        updateAlertBadge();

        const ackTargets = runs.filter((run) => automationAckPendingRef.current.has(run.id));
        if (ackTargets.length > 0) {
          await acknowledgeAutomationRuns(ackTargets);
        }
      } catch (error) {
        console.warn("Impossible de charger les messages d'automatisation Nexus:", error);
      }
    },
    [acknowledgeAutomationRuns, appendAutomationRuns, automationUnread, setAutomationUnread, updateAlertBadge]
  );

  useEffect(() => {
    if (!lastMutation || !lastMutation.metadata) {
      return;
    }
    const entity = (lastMutation.metadata.entity || '').toLowerCase();
    if (entity === 'tasks' || entity === 'objectives' || entity === 'automations') {
      loadProactivityRequests({ silent: true });
      loadAutomationMessages();
    }
  }, [lastMutation, loadProactivityRequests, loadAutomationMessages]);

  useEffect(() => {
    const intervalMs = isOpen ? 30000 : 90000;
    if (proactivityIntervalRef.current) {
      clearInterval(proactivityIntervalRef.current);
    }
    const tick = () => {
      loadProactivityRequests({ silent: true });
    };
    tick();
    proactivityIntervalRef.current = setInterval(tick, intervalMs);
    return () => {
      if (proactivityIntervalRef.current) {
        clearInterval(proactivityIntervalRef.current);
        proactivityIntervalRef.current = null;
      }
    };
  }, [isOpen, loadProactivityRequests]);

  useEffect(() => {
    let timeoutId = null;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) {
        return;
      }
      await loadAutomationMessages();
      if (!cancelled) {
        timeoutId = setTimeout(poll, AUTOMATION_POLL_INTERVAL_MS);
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [loadAutomationMessages]);

  useEffect(() => {
    loadProactivityRequests();
  }, [loadProactivityRequests]);

  useEffect(() => {
    const handler = () => {
      loadProactivityRequests({ silent: true });
      loadAutomationMessages();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('nexus-proactivity-refresh', handler);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('nexus-proactivity-refresh', handler);
      }
    };
  }, [loadProactivityRequests, loadAutomationMessages]);

  const pushAssistantMessage = useCallback((content) => {
    if (!content) return;
    setMessages((prev) => [...prev, { content, role: 'assistant', agent: 'nexus' }]);
  }, [setMessages]);

  const ensureDeepflowProjectId = useCallback(async () => {
    if (deepflowProjectRef.current.id) {
      return deepflowProjectRef.current.id;
    }
    if (deepflowProjectRef.current.promise) {
      return deepflowProjectRef.current.promise;
    }
    const fetchPromise = apiService
      .get('/projects')
      .then((projects) => {
        const list = Array.isArray(projects) ? projects : [];
        const target = list.find((project) => project.name?.toLowerCase().includes('deepflow'));
        if (!target) {
          throw new Error("Impossible de trouver le projet Deepflow.");
        }
        deepflowProjectRef.current.id = target.id;
        return target.id;
      })
      .finally(() => {
        deepflowProjectRef.current.promise = null;
      });
    deepflowProjectRef.current.promise = fetchPromise;
    return fetchPromise;
  }, []);

  const buildConversationLog = useCallback(() => {
    const snapshot = messagesRef.current || [];
    const transcript = snapshot
      .filter(
        (msg) =>
          (msg.role === 'user' || msg.role === 'assistant') &&
          typeof msg.content === 'string' &&
          msg.content.trim()
      )
      .map((msg) => {
        const speaker = msg.role === 'user' ? 'Utilisateur' : 'Nexus';
        const agentSuffix = msg.agent && msg.agent !== 'nexus' ? ` (${msg.agent})` : '';
        return `${speaker}${agentSuffix}: ${msg.content.trim()}`;
      })
      .join('\n');
    return transcript;
  }, []);

  const buildActivityLogSummary = useCallback(() => {
    const snapshot = activityLogRef.current || [];
    if (!snapshot.length) return '';
    return snapshot
      .map((entry) => {
        const ts =
          entry.timestamp instanceof Date ? entry.timestamp : new Date(entry.timestamp || Date.now());
        const tsLabel = Number.isNaN(ts.getTime()) ? '' : ts.toLocaleString('fr-FR');
        const details = entry.details ? ` — ${entry.details}` : '';
        return `[${entry.typeLabel || entry.type || 'MCP'}${tsLabel ? ` · ${tsLabel}` : ''}] ${
          entry.text
        }${details}`;
      })
      .join('\n');
  }, []);

  const toggleChat = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen && messages.length === 0) {
      setMessages([getRandomGreeting()]);
    }
  };

  const clearPendingConfirmation = useCallback(() => {
    setPendingConfirmation(null);
    pendingConfirmationRef.current = null;
  }, []);

  const appendActivity = useCallback((type, text, details, meta = {}) => {
    setActivityLog((prev) => {
      const entry = {
        id: `${Date.now()}-${Math.random()}`,
        type,
        typeLabel: activityLabels[type] || 'Activité',
        text,
        details,
        tool: meta.tool || '',
        toolLabel: meta.toolLabel || '',
        status: meta.status || '',
        metadata: meta.metadata || null,
        timestamp: new Date(),
      };
      const next = [...prev, entry];
      return next.slice(-12);
    });
  }, [activityLabels]);

  // Contexte d'URL, complété par ce que la page publie (document ouvert, slide regardée…).
  const pageContext = useMemo(() => {
    const base = buildPageContext(location);
    if (!pageDetails) return base;
    return {
      ...base,
      description: pageDetails.description || base.description,
      extra: { ...base.extra, ...(pageDetails.extra || {}) },
    };
  }, [location, pageDetails]);


  const handleStreamEvent = useCallback((event) => {
    const { event: type, data } = event;
    if (!type || !data) return;

    if (type === 'message_delta' || type === 'message_snapshot') {
      setMessages((prev) => updateHermesDraft(prev, type, data));
      return;
    }
    if (type === 'message_interim') {
      // Texte écrit avant d'autres outils : c'était un commentaire, pas la réponse.
      setMessages(clearHermesDraft);
      updateRun((run) => applyNote(run || startRun(), data.text));
      appendActivity('activity', 'Nexus', data.text || '', { status: 'running' });
      return;
    }

    if (type === 'activity') {
      const phase = data.phase || 'activity';
      const label = resolveActivityType(phase);
      const text = data.activity || data.tool_label || data.tool || 'Activité Nexus';
      if (phase !== 'heartbeat') {
        appendActivity(label, text, data.details || '', {
          tool: data.tool,
          toolLabel: data.tool_label,
          status: data.status,
          metadata: data.metadata,
        });
      }
      updateRun((run) => applyActivity(run || startRun(), data));
      if (phase === 'tool_result' && data.metadata) {
        publishToolMutation({
          tool: data.tool,
          metadata: data.metadata,
          summary: data.details || data.activity,
        });
      }
      return;
    }

    if (type === 'pending') {
      resumeRef.current.pending = true;
      return;
    }

    if (type === 'confirmation') {
      resumeRef.current.count = 0;
      setMessages(clearHermesDraft);
      const details = (data.summary && data.summary.length && data.summary.join('\n')) || data.message;
      appendActivity('confirmation', 'Confirmation requise', details, {
        tool: data.tool,
        status: data.status || 'waiting',
      });
      const batchId = data.batch_id || data.call_id || `batch-${Date.now()}`;
      const actions = (data.actions || []).map((action) => ({
        tool: action.tool,
        title: action.title,
        summary: action.summary || [],
        callId: action.call_id,
        rawArguments: action.raw_arguments || action.arguments || {},
      }));
      const confirmation = {
        tool: data.tool,
        arguments: data.raw_arguments || data.arguments,
        callId: data.call_id,
        batchId,
        hermesApproval: data.hermes_approval,
        message: data.message,
        summary: data.summary || [],
        title: data.title || data.tool,
        locked: false,
        actions,
        // Carte lisible (2026-10-09) : aperçu de l'action (créneau dans l'agenda), libellé du bouton, expiration.
        preview: data.preview || null,
        confirmLabel: data.confirm_label || null,
        expiresAt: data.expires_at || null,
      };
      setPendingConfirmation(confirmation);
      pendingConfirmationRef.current = confirmation;
      const confirmationRun = takeRun();
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          agent: 'nexus',
          type: 'confirmation',
          confirmation,
          run: confirmationRun,
        },
      ]);
      setLoading(false);
      return;
    }

    if (type === 'completed') {
      flashBot('happy');
      resumeRef.current.count = 0;
      if (pendingConfirmationRef.current && !pendingConfirmationRef.current.locked) {
        setLoading(false);
        return;
      }
      const mutations = Array.isArray(data.mutations) ? data.mutations : [];
      if (mutations.length) {
        mutations.forEach((mutation) => publishToolMutation(mutation));
      }
      appendActivity('completed', 'Réponse générée', data.message?.slice(0, 160) || '', { status: 'success' });
      const completedRun = takeRun();
      setMessages((prev) => [
        ...settleConfirmations(clearHermesDraft(prev)),
        {
          content: data.message || 'Pas de réponse disponible.',
          role: 'assistant',
          agent: data.agent || 'nexus',
          run: completedRun,
        },
      ]);
      setLoading(false);
      clearPendingConfirmation();
      return;
    }

    if (type === 'error') {
      flashBot('sad');
      resumeRef.current.count = 0;
      appendActivity('error', 'Erreur rencontrée', data.message, { status: 'error' });
      clearPendingConfirmation();
      const failedRun = takeRun();
      setMessages((prev) => [
        ...settleConfirmations(clearHermesDraft(prev)),
        {
          content: data.message || "Désolé, une erreur est survenue.",
          role: 'assistant',
          agent: 'nexus',
          isError: true,
          run: failedRun,
        },
      ]);
      setLoading(false);
      return;
    }
  }, [appendActivity, clearPendingConfirmation, flashBot, publishToolMutation, takeRun, updateRun]);

  const streamMessage = useCallback(async (rawMessage, options = {}) => {
    const { force = false, silent = false } = options;
    const message = rawMessage.trim();
    if (!message || (!force && loading)) return;

    clearPendingConfirmation();

    if (readerRef.current) {
      readerRef.current.cancel().catch(() => {});
      readerRef.current = null;
    }

    if (!silent) {
      setMessages((prev) => [...prev, { content: message, role: 'user' }]);
      appendActivity('start', 'Demande envoyée à Nexus', message, { status: 'running' });
      updateRun(() => startRun());
    } else if (!options.resume) {
      appendActivity('start', 'Reprise du traitement Nexus', '', { status: 'running' });
      updateRun(() => startRun());
    }

    setLoading(true);

    try {
      const apiUrl = `${apiService.getBaseUrl()}/chat/stream`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...apiService.getHeaders(),
        },
        body: JSON.stringify({
          message,
          conversation_id: conversationIdRef.current,
          page_context: pageContext,
          ...(options.approval ? { approval: options.approval } : {}),
          ...(chatModeRef.current === 'muse-tools' ? { engine: 'muse' } : {}),
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error("Impossible d'atteindre le service de chat.");
      }

      const reader = response.body.getReader();
      readerRef.current = reader;
      await readSseEvents(reader, handleStreamEvent);
      // Toujours en cours côté Hermes : reprise silencieuse du suivi (sans message utilisateur),
      // plafonnée pour ne jamais boucler indéfiniment.
      if (resumeRef.current.pending) {
        resumeRef.current.pending = false;
        if (resumeRef.current.count < 30) {
          resumeRef.current.count += 1;
          setTimeout(() => streamMessageRef.current?.('reprendre', { force: true, silent: true, resume: true }), 1000);
        } else {
          resumeRef.current.count = 0;
          setLoading(false);
          takeRun();
        }
      }
    } catch (error) {
      console.error('Erreur streaming Nexus:', error);
      appendActivity('error', 'Erreur réseau', error.message, { status: 'error' });
      const brokenRun = takeRun();
      setMessages((prev) => [
        ...prev,
        {
          content: "Désolé, je n'ai pas pu contacter le service. Réessaie dans un instant.",
          role: 'assistant',
          agent: 'nexus',
          isError: true,
          run: brokenRun,
        },
      ]);
      setLoading(false);
    } finally {
      if (readerRef.current) {
        readerRef.current.cancel().catch(() => {});
        readerRef.current = null;
      }
    }
  }, [appendActivity, clearPendingConfirmation, handleStreamEvent, loading, pageContext, takeRun, updateRun]);
  streamMessageRef.current = streamMessage;

  const startBugReportFlow = useCallback(async () => {
    if (bugReportInitiating || loading) return;
    setBugReportInitiating(true);
    pushAssistantMessage(
      'Je lance le mode reporter de bug. Nexus va te poser les questions nécessaires.'
    );
    try {
      const projectId = await ensureDeepflowProjectId();
      const conversationLog = buildConversationLog();
      const activitySummary = buildActivityLogSummary();

      const userIdentity = user?.name || user?.email || 'Utilisateur Nexus';
      const instructions = [
        'Raccourci déclenché : Reporter un bug.',
        'Objectif : Tu prends entièrement la main sur la discussion pour qualifier le bug, proposer un titre clair et créer un objectif dans le projet Deepflow.',
        '',
        'Procédure attendue :',
        '1. Demande d’abord si le bug concerne Deepflow (site/app) ou Nexus (IA).',
        "2. Pose les questions nécessaires (contexte, reproduction, impact, environnement) jusqu'à avoir assez d'information.",
        '3. Reformule la description en français clair et propose un titre court et percutant.',
        `4. Crée un objectif dans le projet Deepflow (ID ${projectId}) via l’outil objectives__create en respectant ce format de nom : "[BUG] [NEXUS|DEEPFLOW] {titre}". Le workflow bug attribue automatiquement l'objectif a l'employe #1 (owner_id: 1) et abonne le reporteur, pas besoin de le faire manuellement.`,
        '5. Dans la description de l’objectif, inclue : origine (Deepflow ou Nexus), auteur du signalement, date, description reformulée, étapes clés et impact.',
        "6. Si le bug concerne Nexus, ajoute OBLIGATOIREMENT dans la description un bloc '--- Historique conversation ---' avec le log ci-dessous, puis un bloc '--- Journal MCP ---' avec le journal ci-dessous.",
        '7. Confirme à l’utilisateur quand le ticket est créé.',
        '',
        `Identité de l’utilisateur : ${userIdentity}`,
        '',
        'Historique de conversation actuel (à inclure uniquement pour un bug Nexus) :',
        conversationLog || 'Aucun échange exploitable.',
        '',
        'Journal MCP actuel (à inclure uniquement pour un bug Nexus) :',
        activitySummary || 'Aucun appel MCP enregistré.',
        '',
        'Ajoute aussi les échanges et appels qui auront lieu après cette consigne si le bug concerne Nexus.',
      ].join('\n');

      await streamMessage(instructions, { force: true, silent: true });
    } catch (error) {
      console.error('Bug report trigger failed', error);
      pushAssistantMessage("Impossible de lancer le mode bug reporter pour l'instant.");
    } finally {
      setBugReportInitiating(false);
    }
  }, [
    bugReportInitiating,
    loading,
    pushAssistantMessage,
    ensureDeepflowProjectId,
    buildConversationLog,
    buildActivityLogSummary,
    streamMessage,
    user,
  ]);

  const bugShortcutDisabled = bugReportInitiating || loading;

  const lockConfirmationMessage = (batchId) => {
    if (!batchId) return;
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.type === 'confirmation' && msg.confirmation?.batchId === batchId) {
          return {
            ...msg,
            confirmation: { ...msg.confirmation, locked: true },
          };
        }
        return msg;
      })
    );
  };

  const handleConfirmation = (accepted, confirmationFromCard = null) => {
    const confirmation = pendingConfirmation || confirmationFromCard;
    if (!confirmation) return;
    const reply = accepted ? 'oui' : 'non';
    lockConfirmationMessage(confirmation.batchId);
    const lockedConfirmation = { ...confirmation, locked: true };
    setPendingConfirmation(lockedConfirmation);
    pendingConfirmationRef.current = lockedConfirmation;
    streamMessage(reply, {
      force: true,
      silent: true,
      ...(confirmation.hermesApproval ? {
        approval: { ...confirmation.hermesApproval, choice: accepted ? 'once' : 'deny' },
      } : {}),
    });
  };

  const handleProactivityDecision = useCallback(
    async (request, decision) => {
      if (!request || !decision) {
        return;
      }
      setRespondingProactivityId(request.id);
      try {
        await apiService.respondToNexusProactivityRequest(request.id, decision);
        setProactivityRequests((prev) => prev.filter((item) => item.id !== request.id));
        if (decision === 'accept') {
          pushAssistantMessage(
            `Parfait, je clos l'objectif « ${request.objective_name} ».`,
          );
        } else {
          pushAssistantMessage(`Très bien, on garde « ${request.objective_name} » ouvert pour l'instant.`);
        }
      } catch (error) {
        console.error('Impossible de traiter la demande Nexus:', error);
        pushAssistantMessage("Oups, je n'ai pas pu traiter cette demande. Réessaie dans un instant.");
      } finally {
        setRespondingProactivityId(null);
        loadProactivityRequests({ silent: true });
      }
    },
    [loadProactivityRequests, pushAssistantMessage]
  );

  const handleClearConversation = async () => {
    if (clearingConversation) return;

    if (readerRef.current) {
      readerRef.current.cancel().catch(() => {});
      readerRef.current = null;
    }

    setLoading(false);

    const previousConversationId = conversationIdRef.current;
    setClearingConversation(true);
    try {
      await apiService.post('/chat/reset', {
        conversation_id: previousConversationId,
        ...(chatModeRef.current === 'muse-tools' ? { engine: 'muse' } : {}),
      });
    } catch (error) {
      console.warn('Échec de la réinitialisation de la conversation Nexus:', error);
      pushAssistantMessage(error?.response?.data?.detail || error?.message ||
        'La conversation reste ouverte. Réessaie après l’arrêt du traitement.');
      return;
    } finally {
      setClearingConversation(false);
    }

    clearPendingConfirmation();
    pendingConfirmationRef.current = null;
    takeRun();
    setActivityLog([]);
    setMessages([chatModeRef.current === 'muse-tools' ? MUSE_TOOLS_GREETING : getRandomGreeting()]);
    setBugReportInitiating(false);
    const newConversationId = generateConversationId();
    setConversationId(newConversationId);
  };

  const isMuse = chatMode === 'muse';
  const isMuseTools = chatMode === 'muse-tools';
  const clearConversationLabel = clearingConversation ? 'Réinitialisation…' : 'Nouvelle conversation';
  const pendingAlertCount = proactivityRequests.length + automationUnread + unseenNotices.length;
  const modeSwitchLabel = isMuse || isMuseTools ? 'Revenir à Nexus' : 'Parler à Muse (Mac mini)';

  useEffect(() => {
    if (chatMode === 'muse' || chatMode === lastPanelRef.current) return;
    threadsRef.current[lastPanelRef.current] = { messages, conversationId, activityLog };
    const saved = threadsRef.current[chatMode];
    setMessages(saved?.messages || [chatMode === 'muse-tools' ? MUSE_TOOLS_GREETING : getRandomGreeting()]);
    setConversationId(saved?.conversationId || generateConversationId());
    setActivityLog(saved?.activityLog || []);
    lastPanelRef.current = chatMode;
  }, [chatMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNewConversation = () => {
    if (isMuse) museConversation.reset();
    else handleClearConversation();
  };

  // Le brouillon d'Hermes s'affiche dans la réponse en cours, avec ses étapes, pas dans la liste des messages.
  const draftMessage = messages.find((msg) => msg.streaming);
  // Chat fermé avec quelque chose pour la personne : Nexus tient une lettre (antenne orange, petits sauts).
  const botMood = botFlash || (draftMessage ? 'writing' : loading ? 'thinking'
    : !isOpen && pendingAlertCount > 0 ? 'message' : 'idle');
  const visibleMessages = messages.filter((msg) => !msg.streaming);
  const lastUserMessage = [...visibleMessages].reverse().find((msg) => msg.role === 'user')?.content || '';
  const emptyConversation = visibleMessages.length <= 1 && !loading && proactivityRequests.length === 0;
  const panelSize = expanded
    ? 'h-[calc(100vh-2rem)] w-[min(64rem,calc(100vw-2rem))]'
    : 'h-[min(42rem,calc(100vh-2rem))] w-[min(44rem,calc(100vw-1rem))]';
  const headerButton = 'relative flex h-8 w-8 items-center justify-center rounded-full text-white/90 transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-50';

  const pickSuggestion = (suggestion) => {
    if (suggestion.endsWith('…')) setDraft({ text: suggestion.slice(0, -1) + ' ' });
    else streamMessage(suggestion);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="group relative flex h-14 w-14 items-center justify-center rounded-full border border-indigo-100 bg-white shadow-lg shadow-indigo-500/20 ring-4 ring-indigo-500/10 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-indigo-500/30"
          aria-label="Ouvrir Nexus"
        >
          <NexusBot size={46} mood={botMood} track pathname={location.pathname} />
          {nexusAlertPending && pendingAlertCount > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white ring-2 ring-white">
              {Math.min(99, pendingAlertCount)}
            </span>
          )}
        </button>
      )}
      {!isOpen && unseenNotices.length > 0 && (
        <button type="button" onClick={toggleChat}
          className="absolute bottom-2 right-[4.25rem] w-max max-w-[min(16rem,calc(100vw-6rem))] rounded-2xl rounded-br-sm border border-amber-200 bg-white px-3 py-2 text-left text-xs shadow-lg shadow-amber-500/10 transition hover:border-amber-300">
          <span className="block font-semibold text-amber-600">Nexus a un message pour toi</span>
          <span className="block truncate text-slate-600">{unseenNotices[0].title}{unseenNotices.length > 1 ? ` (+${unseenNotices.length - 1})` : ''}</span>
        </button>
      )}

      {isOpen && (
        <div className={`absolute bottom-0 right-0 flex max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-2xl ring-1 ring-black/5 ${panelSize} max-sm:fixed max-sm:inset-x-0 max-sm:top-0 max-sm:h-[100dvh] max-sm:w-full max-sm:max-w-none max-sm:rounded-none max-sm:border-0`}
          style={phoneBox ? { top: phoneBox.top, height: phoneBox.height, bottom: 'auto' } : undefined}>
          <header
            className={`shrink-0 bg-gradient-to-r px-3 pb-2.5 pt-[max(0.625rem,env(safe-area-inset-top))] text-white sm:px-4 ${
              isMuse || isMuseTools ? 'from-slate-800 via-teal-700 to-cyan-600' : 'from-indigo-600 via-violet-600 to-blue-500'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${isMuse || isMuseTools ? 'bg-white/20 backdrop-blur' : ''}`}>
                {isMuse || isMuseTools ? (
                  <Sparkles className="h-5 w-5" />
                ) : (
                  <NexusBot size={36} mood={botMood} track pathname={location.pathname} className="drop-shadow" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="flex items-center gap-2 text-base font-semibold leading-5">
                  {isMuse || isMuseTools ? 'Muse' : 'Nexus'}
                  {(isMuse || isMuseTools) && canTryMuseTools && (
                    <button type="button" onClick={() => setChatMode(isMuseTools ? 'muse' : 'muse-tools')}
                      title={isMuseTools ? 'Revenir à Muse sans outils' : 'Essai : Muse avec les outils de Nexus'}
                      className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition ${
                        isMuseTools ? 'border-white/70 bg-white text-teal-800' : 'border-white/40 bg-white/10 text-white hover:bg-white/20'}`}>
                      {isMuseTools ? 'Outils · essai' : '+ Outils'}
                    </button>
                  )}
                  {!isMuse && loading && (
                    <span className="flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-medium">
                      <Loader2 className="h-3 w-3 animate-spin" /> au travail
                    </span>
                  )}
                </h3>
                {isMuse || isMuseTools ? (
                  <p className="flex items-center gap-1.5 text-xs text-teal-50">
                    <span className={`inline-block h-1.5 w-1.5 rounded-full ${museStatusDotClasses[museStatus]}`} />
                    {isMuseTools ? 'Avec les outils de Nexus, sur le Mac mini' : 'Modèle local sur le Mac mini'} · {museStatusLabels[museStatus]}
                  </p>
                ) : (
                  <>
                    <p className="truncate text-xs text-white/75">Projets, documents et actions Deepflow</p>
                    <div className="hidden sm:block"><NexusModelSwitch open={isOpen} /></div>
                  </>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                {!isMuse && !isMuseTools && (
                  <button type="button" onClick={startBugReportFlow} disabled={bugShortcutDisabled} className={`${headerButton} max-sm:hidden`}
                    title={bugReportInitiating ? 'Préparation…' : 'Reporter un bug'} aria-label="Reporter un bug">
                    <Bug className="h-4 w-4" />
                  </button>
                )}
                <button type="button" onClick={() => setChatMode(isMuse || isMuseTools ? 'nexus' : 'muse')}
                  className={headerButton} aria-label={modeSwitchLabel} title={modeSwitchLabel}>
                  {isMuse || isMuseTools ? <NexusBot size={18} still title="Revenir à Nexus" pathname={location.pathname} /> : <Sparkles className="h-4 w-4" />}
                  {/* Pastille : Nexus attend une réponse, ou Muse répond pendant qu'on est sur Nexus. */}
                  {(isMuse || isMuseTools) && pendingAlertCount > 0 && (
                    <span className="absolute right-0.5 top-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white/80" />
                  )}
                  {!isMuse && museConversation.pending && (
                    <span className="absolute right-0.5 top-0.5 h-2.5 w-2.5 animate-pulse rounded-full bg-teal-300 ring-2 ring-white/80" />
                  )}
                </button>
                <button type="button" onClick={handleNewConversation} disabled={!isMuse && clearingConversation}
                  className={headerButton} aria-label={clearConversationLabel} title={clearConversationLabel}>
                  {!isMuse && clearingConversation ? <Loader2 className="h-4 w-4 animate-spin" /> : <SquarePen className="h-4 w-4" />}
                </button>
                <button type="button" onClick={() => setExpanded((value) => !value)} className={`${headerButton} max-sm:hidden`}
                  aria-label={expanded ? 'Réduire le chat' : 'Agrandir le chat'} title={expanded ? 'Réduire' : 'Agrandir'}>
                  {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </button>
                <button type="button" onClick={() => setIsOpen(false)} className={headerButton}
                  aria-label="Fermer le chat" title="Fermer">
                  <X className="h-5 w-5 sm:h-4 sm:w-4" />
                </button>
              </div>
            </div>
          </header>

          {isMuse ? (
            <MuseChat conversation={museConversation} status={museStatus} />
          ) : (
            <>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-gradient-to-b from-slate-50/90 via-white to-white">
                <div className={`mx-auto space-y-5 px-3 py-4 sm:px-4 sm:py-5 ${expanded ? 'max-w-3xl' : ''}`}>
                  {visibleMessages.map((msg, idx) => {
                    if (msg.type === 'confirmation' && msg.confirmation) {
                      const isForeignCard =
                        pendingConfirmation &&
                        pendingConfirmation.batchId !== msg.confirmation.batchId;
                      return (
                        <div key={`confirm-${idx}`} className="flex items-start gap-3">
                                                    <div className="min-w-0 flex-1">
                            {msg.run && <RunSteps run={msg.run} writing />}
                            <ConfirmationPrompt
                              prompt={msg.confirmation}
                              onConfirm={() => handleConfirmation(true, msg.confirmation)}
                              onCancel={() => handleConfirmation(false, msg.confirmation)}
                              disabled={Boolean(isForeignCard)}
                            />
                          </div>
                        </div>
                      );
                    }
                    // La dernière réponse en erreur : « Réessayer » renvoie la dernière demande (le modèle a pu être
                    // momentanément saturé, ex. quota par minute de Scaleway).
                    const retry = msg.isError && idx === visibleMessages.length - 1 && !loading && lastUserMessage
                      ? () => streamMessage(lastUserMessage) : null;
                    return <ChatBubble key={`msg-${idx}`} message={msg} onRetry={retry} />;
                  })}

                  {emptyConversation && (
                    <div className="flex flex-wrap gap-2 sm:pl-10">
                      {suggestionsFor(pageContext).map((suggestion) => (
                        <button key={suggestion} type="button" onClick={() => pickSuggestion(suggestion)}
                          className="rounded-full border border-indigo-100 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-50">
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}

                  {proactivityLoading && proactivityRequests.length === 0 && (
                    <div className="flex items-center gap-3 rounded-2xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-indigo-600">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm font-medium">Je vérifie si des actions Nexus t'attendent…</span>
                    </div>
                  )}

                  {notices.map((notice) => (
                    <div key={`notice-${notice.id}`} className="flex items-start gap-3">
                      <NexusNoticeCard notice={notice} onDismiss={dismissNotice} />
                    </div>
                  ))}

                  {proactivityRequests.map((request) => (
                    <div key={`proactivity-${request.id}`} className="flex items-start gap-3">
                                            <ProactivityPrompt
                        request={request}
                        onAccept={() => handleProactivityDecision(request, 'accept')}
                        onDecline={() => handleProactivityDecision(request, 'decline')}
                        loading={respondingProactivityId === request.id}
                      />
                    </div>
                  ))}

                  {(loading || draftMessage) && liveRun && (
                    <ChatBubble
                      live
                      message={{
                        role: 'assistant',
                        agent: 'nexus',
                        content: draftMessage?.content || '',
                        streaming: Boolean(draftMessage),
                        run: liveRun,
                      }}
                    />
                  )}

                  <div ref={messagesEndRef} />
                </div>
              </div>

              <ChatInput onSendMessage={streamMessage} isLoading={loading} draft={draft} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatBot;
